import { Kafka, Partitioners, type Producer, type RecordMetadata, type SASLOptions } from 'kafkajs';
import type { TestEvent } from '../types/index.js';
import type { Logger } from '../utils/logger.js';
import { createNoopLogger } from '../utils/logger.js';
import { suppressKafkaTimeoutWarning, restoreKafkaTimeoutWarning } from '../utils/kafka-timeout-warning.js';

export interface KafkaProducerOptions {
  brokers: string[];
  channel: string;
  clientId?: string;
  sasl?: SASLOptions;
  ssl?: boolean;
  connectionTimeout?: number;
  requestTimeout?: number; // Azure Event Hubs recommends 60_000.
  debug?: boolean;
  logger?: Logger;
}

export class KafkaEventProducer {
  private kafka: Kafka;
  private producer: Producer | null = null;
  private channel: string;
  private options: KafkaProducerOptions;
  private readonly logger: Logger;
  private connected = false;
  private connectPromise: Promise<void> | null = null;

  constructor(options: KafkaProducerOptions) {
    this.options = options;
    this.channel = options.channel;
    this.logger = options.logger ?? createNoopLogger();

    this.kafka = new Kafka({
      clientId: options.clientId ?? 'testdino-reporter',
      brokers: options.brokers,
      connectionTimeout: options.connectionTimeout ?? 10_000,
      requestTimeout: options.requestTimeout ?? 60_000,
      enforceRequestTimeout: true,
      ...(options.sasl && { sasl: options.sasl }),
      ...(options.ssl !== undefined && { ssl: options.ssl }),
    });
  }

  async connect(): Promise<void> {
    if (this.connected) return;
    // Serialize concurrent callers.
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = this.doConnect();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async doConnect(): Promise<void> {
    // Install BEFORE connect: kafkajs schedules the negative-delay requestQueue
    // timer while the connection handshake drains, so a post-connect install
    // misses the warning it fires during connect().
    suppressKafkaTimeoutWarning();
    const producer = this.kafka.producer({
      createPartitioner: Partitioners.DefaultPartitioner,
    });
    try {
      await producer.connect();
    } catch (error) {
      // Leave the warning filter installed — a failed attempt may be followed by
      // a reconnect (Event Hubs resets flap connections). Only close() tears it
      // down; the reporter always close()s, even on the HTTP-fallback path.
      await producer.disconnect().catch(() => {});
      throw error;
    }
    this.producer = producer;
    this.connected = true;
    this.logger.debug(`Kafka connected to ${this.options.brokers.join(', ')}`);
  }

  async send(event: TestEvent): Promise<RecordMetadata[]> {
    const producer = this.producer;
    if (!producer) throw new Error('KafkaEventProducer not connected — call connect() first');

    try {
      return await producer.send({
        topic: this.channel,
        acks: 1,
        messages: [{ key: event.runId, value: JSON.stringify(event) }],
      });
    } catch (error) {
      this.connected = false;
      const orphaned = this.producer;
      this.producer = null;
      orphaned?.disconnect().catch(() => {});
      throw error;
    }
  }

  // On error: resets connected + nulls producer so the delivery-manager retry
  // reconnects with a fresh producer instance.
  async sendBatch(events: TestEvent[]): Promise<RecordMetadata[]> {
    const producer = this.producer;
    if (!producer) throw new Error('KafkaEventProducer not connected — call connect() first');
    if (events.length === 0) return [];

    try {
      return await producer.send({
        topic: this.channel,
        acks: 1,
        messages: events.map((event) => ({ key: event.runId, value: JSON.stringify(event) })),
      });
    } catch (error) {
      this.connected = false;
      const orphaned = this.producer;
      this.producer = null;
      orphaned?.disconnect().catch(() => {});
      throw error;
    }
  }

  async close(): Promise<void> {
    if (this.producer) {
      const producer = this.producer;
      this.producer = null;
      this.connected = false;
      try {
        await producer.disconnect();
      } catch {
        // Ignore EPIPE — connection may already be dead.
      }
      this.logger.debug('Kafka producer closed');
    }
    restoreKafkaTimeoutWarning();
  }

  get isConnected(): boolean {
    return this.connected;
  }
}
